import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { colecciones, coleccionDestacada } from '../data/colecciones';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';

const coleccionUrl = (c) => c.externalUrl || c.url;

/* Estado: punto + texto sin colores llamativos */
const EstadoDot = ({ estado, variant = 'default' }) => {
  const map = {
    publicada: { dot: 'bg-emerald-500', label: 'Publicada' },
    'en-desarrollo': { dot: 'bg-amber-400', label: 'En desarrollo' },
    proximamente: { dot: 'bg-slate-300', label: 'Próximamente' },
  };
  const { dot, label } = map[estado] || map.proximamente;
  const textColor = variant === 'light' ? 'text-white/55' : 'text-slate-500';
  return (
    <span className={`flex items-center gap-1.5 text-xs ${textColor} font-medium`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
};

/* ─── Franja visual: Gestión de Proyectos de IA ──────────── */
const FranjaIA = () => (
  <div className="w-full h-full bg-[#0A1628] relative overflow-hidden">
    {/* Línea de acento azul — sistema visual */}
    <div className="absolute top-0 left-0 w-[40%] h-[3px] bg-[#2F5DA8]" />
    {/* Elemento tipográfico de fondo */}
    <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center select-none pointer-events-none">
      <span className="font-bold text-white leading-none" style={{ fontSize: '80px', opacity: 0.09 }}>IA</span>
    </div>
  </div>
);

/* ─── Franja visual: 7AM Executive Library ───────────────── */
const Franja7AM = () => (
  <div className="w-full h-full bg-[#0A1A10] relative overflow-hidden">
    {/* Línea de acento verde — sistema visual */}
    <div className="absolute top-0 left-0 w-[40%] h-[3px] bg-[#1A5C3A]" />
    {/* Elemento tipográfico de fondo */}
    <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center select-none pointer-events-none">
      <span className="font-bold text-white leading-none" style={{ fontSize: '80px', opacity: 0.09 }}>7AM</span>
    </div>
  </div>
);

const ColeccionesIndex = () => (
  <div className="min-h-screen bg-white">
    <SEOHead
      title="Colecciones | Edutech Pulse Lab"
      description="Series de contenido organizadas para comprender un tema en profundidad, seguir una ruta de lectura y conectar artículos, conceptos y marcos de trabajo."
      keywords="colecciones educativas, ADO, gestión proyectos IA, 7AM library, series editoriales, innovación, liderazgo"
      canonicalUrl="/colecciones"
    />
    <Navbar />

    {/* ── Hero ──────────────────────────────────────────────────── */}
    <section className="bg-[#081827] text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-6 sm:pt-11 sm:pb-9 lg:pt-12 lg:pb-10">
        <p className="text-xs font-semibold tracking-[0.2em] text-white/40 uppercase mb-5">
          Edutech Pulse Lab · Colecciones
        </p>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-black leading-[1.08] tracking-tight text-white mb-5 max-w-2xl">
          Conocimiento<br />
          <span className="text-white/65 font-light">organizado para</span><br />
          <span className="text-white">decidir mejor.</span>
        </h1>
        <p className="text-sm sm:text-base text-white/50 max-w-lg leading-relaxed font-light">
          Colecciones conectadas para comprender cómo se toman decisiones,
          por qué las iniciativas pierden impulso y cómo convertir ideas,
          marcos y casos en acción.
        </p>
      </div>
    </section>

    {/* ── Colección destacada ────────────────────────────────────── */}
    <section className="border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-14">
        <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-7">
          Colección destacada
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border border-slate-200 rounded-[18px] overflow-hidden">
          {/* Columna izquierda — contenido */}
          <div className="lg:col-span-3 bg-[#0c203b] py-8 px-7 sm:p-10 lg:p-14 flex flex-col justify-between min-h-[340px] relative overflow-hidden">
            {/* Línea de acento dorado — sistema visual */}
            <div className="absolute top-0 left-0 w-[40%] h-[3px] bg-[#C4933A]" />
            {/* Patrón geométrico estructural */}
            <div className="absolute top-0 right-0 w-48 h-64 pointer-events-none select-none opacity-[0.35] sm:opacity-100" aria-hidden="true">
              <svg viewBox="0 0 192 256" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                {/* Eje vertical dorado — referencia principal */}
                <line x1="40" y1="0" x2="40" y2="256" stroke="#C4933A" strokeWidth="1.5" strokeOpacity="0.22"/>
                {/* Capa 1 — marco exterior parcial */}
                <line x1="40" y1="34" x2="191" y2="34" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.10"/>
                <line x1="188" y1="34" x2="188" y2="148" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.10"/>
                {/* Conector diagonal */}
                <line x1="40" y1="58" x2="85" y2="80" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.06"/>
                {/* Capa 2 — marco intermedio */}
                <line x1="40" y1="80" x2="85" y2="80" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.08"/>
                <line x1="85" y1="80" x2="85" y2="200" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.10"/>
                <line x1="85" y1="200" x2="188" y2="200" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.08"/>
                <line x1="188" y1="148" x2="188" y2="200" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.08"/>
                {/* Capa 3 — marco interior */}
                <line x1="130" y1="108" x2="188" y2="108" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.10"/>
                <line x1="130" y1="108" x2="130" y2="172" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.10"/>
                <line x1="130" y1="172" x2="188" y2="172" stroke="#8DB8D4" strokeWidth="1" strokeOpacity="0.08"/>
              </svg>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-0.5 h-4 bg-amber-400 rounded-full flex-shrink-0" />
                <span className="text-xs font-semibold tracking-[0.2em] text-white/75 uppercase">
                  {coleccionDestacada.nombre}
                </span>
              </div>

              <p className="text-xs font-medium text-white/40 mb-2 tracking-wide">Una colección de David Prudencio</p>
              <h2 className="text-[2rem] sm:text-3xl lg:text-4xl font-bold text-white leading-[1.12] mb-4 max-w-sm">
                {coleccionDestacada.nombreCompleto}
              </h2>
              <p className="text-white/70 text-lg leading-[1.55] max-w-lg font-light">
                {coleccionDestacada.tagline}
              </p>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-white/40">
                Serie de {coleccionDestacada.articuloCount} artículos · {coleccionDestacada.conceptoCount} conceptos
              </p>
              {coleccionUrl(coleccionDestacada) && (
                <Link
                  to={coleccionUrl(coleccionDestacada)}
                  className="group inline-flex items-center justify-center gap-2 text-white text-sm font-semibold border border-white/25 hover:border-white/60 px-5 py-4 sm:py-2.5 rounded-lg transition-all duration-200 w-full sm:w-auto"
                >
                  Explorar colección
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              )}
            </div>
          </div>

          {/* Columna derecha — preview de artículos */}
          <div className="lg:col-span-2 bg-slate-50 py-6 px-5 sm:p-10 lg:p-12 flex flex-col justify-between">
            <div>
              <p className="text-xs font-semibold tracking-[0.15em] text-slate-400 uppercase mb-6">
                Artículos de la colección
              </p>
              <ol className="divide-y divide-slate-200">
                {coleccionDestacada.articulos.slice(0, 3).map((art) => (
                  <li key={art.numero} className="py-[22px] first:pt-0">
                    <span className="block text-sm font-semibold text-slate-500 mb-2 tracking-wide">
                      {coleccionDestacada.nombre} {art.numero}
                    </span>
                    <span className="block text-[20px] leading-[1.35] text-slate-800 font-medium">{art.titulo}</span>
                  </li>
                ))}
              </ol>
            </div>
            {coleccionDestacada.articulos.length > 3 && coleccionUrl(coleccionDestacada) && (
              <Link
                to={coleccionUrl(coleccionDestacada)}
                className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors group border-t border-slate-200 pt-4"
              >
                Ver {coleccionDestacada.articulos.length - 3} artículos más
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>

    {/* ── Otras colecciones ──────────────────────────────────── */}
    <section>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-14">
        <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-8">
          Otras colecciones
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {colecciones.filter((col) => !col.destacada).map((col) => {
            const href = coleccionUrl(col);
            const publicados = col.articuloCount ?? 0;
            const total = col.totalArticulos ?? publicados;
            const unidad = col.unidadContenido || 'artículos';
            const ctaLabel = unidad === 'audioartículos' ? 'Escuchar audioartículos' : 'Explorar colección';
            const disponible = publicados === 1 ? 'disponible' : 'disponibles';

            const card = (
              <div className={`flex flex-col h-full border border-slate-200 rounded-[18px] overflow-hidden transition-all duration-200 shadow-[0_4px_18px_rgba(0,0,0,0.04)] ${href ? 'group-hover:border-slate-300' : ''}` }>
                {/* Franja visual superior */}
                <div className="h-24 flex-shrink-0">
                  {col.slug === 'proyectos-de-ia' && <FranjaIA />}
                  {col.slug === '7am-library' && <Franja7AM />}
                </div>

                {/* Contenido */}
                <div className="flex flex-col flex-1 bg-white py-7 px-6 sm:py-8 sm:px-7">
                  <p className="text-xs font-medium text-slate-400 mb-1 tracking-wide">Una colección de David Prudencio</p>
                  <h3 className={`text-[26px] leading-[1.25] font-bold mt-1 mb-2 transition-colors ${href ? 'text-[#0c203b] group-hover:text-slate-700' : 'text-slate-700'}`}>
                    {col.nombre}
                  </h3>

                  <p className="text-lg leading-[1.5] text-slate-600 flex-1 mb-4">
                    {col.descripcion}
                  </p>

                  <p className="text-sm font-medium text-slate-500 mb-5">
                    {publicados} de {total} {unidad} {disponible}
                  </p>

                  {/* CTA */}
                  {href && (
                    <div className="flex items-center gap-1.5 text-base font-semibold text-[#0c203b] border-t border-slate-100 pt-4 group-hover:gap-2 transition-all duration-200">
                      {ctaLabel}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  )}
                </div>
              </div>
            );

            return href ? (
              <Link key={col.slug} to={href} className="group block">
                {card}
              </Link>
            ) : (
              <div key={col.slug}>{card}</div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ── CTA ───────────────────────────────────────────────────── */}
    {/* ── Cómo se conectan ──────────────────────────────────── */}
    <section className="border-t border-slate-100 bg-slate-50/60">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-14">
        <p className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase mb-5">
          Cómo se conectan
        </p>
        <p className="text-base sm:text-lg text-[#4C586A] leading-relaxed max-w-3xl">
          ADO construye el marco para entender qué sostiene o debilita una iniciativa. Gestión de Proyectos de IA lleva ese criterio a decisiones sobre viabilidad, datos, riesgos y operación. 7AM Executive Library amplía el territorio con ideas de autores sobre liderazgo, innovación, sistemas y toma de decisiones.
        </p>
      </div>
    </section>

    {/* ── Micro-Programas ───────────────────────────────────────── */}
    <section className="bg-[#081827]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] text-white/30 uppercase mb-4">
            Micro-Programas
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight max-w-lg">
            Del conocimiento a la práctica deliberada.
          </h2>
        </div>
        <Link
          to="/programas"
          className="group flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#081827] font-semibold rounded-xl hover:bg-slate-100 transition-colors text-sm sm:text-base"
        >
          Ver Micro-Programas
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </section>
  </div>
);

export default ColeccionesIndex;
